import { Controller } from "@hotwired/stimulus"
import { createPopper } from "@popperjs/core"

/**
 * helplink - popover „?" z podpowiedzią. Dwa tryby, rozpoznawane po targetach:
 *
 * 1. Stary (components/tailwind/helplink, mockupy design/): targets helplink + button, akcja
 *    click->helplink#openPopover z params id - kilka tooltipów na kontroler, wideo w iframe
 *    zatrzymywane przy przełączaniu.
 * 2. Nowy (components/tailwind/helplink_inner): targets button + tooltip (+ title, content, more),
 *    akcja click->helplink#toggle na „?" i „×"; klik poza kontrolerem i Escape zamykają, jeden
 *    kontroler = jeden popover. Treść jest w HTML albo dociągana przy pierwszym otwarciu z value
 *    `url` (JSON `/<modul>/help/<strona>?helplink=<klucz>` -> { key, title, content, path, active },
 *    KbInner::Helplinks - te same pola co helplinki Kb). Odpowiedź wypełnia title/content/more;
 *    błąd pokazuje `data-error-text`
 *    targetu content i pozwala spróbować przy kolejnym otwarciu.
 *    `data-show="true"` na tooltipie otwiera go od razu po połączeniu.
 */
export default class extends Controller {
  static targets = ["helplink", "button", "tooltip", "title", "content", "more"]
  static values = { url: String }

  lastTooltipID = null
  popper = null
  loaded = false
  loading = false

  connect() {
    this.onDocumentClick = this.onDocumentClick.bind(this)
    this.onKeydown = this.onKeydown.bind(this)

    if (this.hasHelplinkTarget && this.helplinkTarget.dataset.show) {
      this.openPopover({ params: { id: this.helplinkTarget.id }, target: this.buttonTarget })
    } else if (this.hasTooltipTarget && this.tooltipTarget.dataset.show === "true") {
      this.open()
    }
  }

  disconnect() {
    if (this.hasTooltipTarget) {
      this.close()
    }
  }

  // ---- stary tryb: kilka tooltipów po id (components/tailwind/helplink)

  openPopover(event) {
    const tooltipID = event.params.id

    if (this.lastTooltipID && this.lastTooltipID !== tooltipID) {
      document.getElementById(this.lastTooltipID).classList.toggle("hidden")
      this.stopVideo(this.lastTooltipID)
    }

    const element = this.getElement(event)

    createPopper(element, document.getElementById(tooltipID), {
      placement: this.helplinkTarget.dataset.placement,
      modifiers: [
        { name: "offset", options: { offset: [0, 8] } }, // 8px odstęp od przycisku
        { name: "flip", options: { fallbackPlacements: ["top", "bottom", "left", "right"] } }, // auto-flip przy krawędzi
        { name: "shift", options: { padding: 8 } }, // przesunięcie gdy za blisko krawędzi
      ]
    })

    const currentTooltipHidden = document.getElementById(tooltipID).classList.toggle("hidden")
    if (currentTooltipHidden === true) {
      this.lastTooltipID = null
    } else {
      this.lastTooltipID = tooltipID
      this.stopVideo(tooltipID)
    }
  }

  stopVideo(videoContainerID) {
    const containerElement = document.getElementById(videoContainerID)
    const iframeTag = containerElement.querySelector("iframe")
    if (iframeTag) {
      const iframeSrc = iframeTag.src
      iframeTag.src = iframeSrc
    }
  }

  getElement(event) {
    let element = event.target
    if (element.dataset.elementId) {
      return document.getElementById(element.dataset.elementId)
    } else {
      while (element.nodeName !== "BUTTON") {
        element = element.parentNode
      }

      return element
    }
  }

  // ---- nowy tryb: jeden popover (components/tailwind/helplink_inner)

  // Przełącza popover - jeden handler dla „?" i „×"
  toggle(event) {
    event.preventDefault()
    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  get isOpen() {
    return !this.tooltipTarget.classList.contains("hidden")
  }

  open() {
    const tooltip = this.tooltipTarget
    this.popper?.destroy()
    this.popper = createPopper(this.buttonTarget, tooltip, {
      placement: tooltip.dataset.placement || "bottom",
      modifiers: [
        { name: "offset", options: { offset: [0, 8] } },
        { name: "flip", options: { fallbackPlacements: ["top", "bottom", "left", "right"] } },
        { name: "shift", options: { padding: 8 } },
      ],
    })
    tooltip.classList.remove("hidden")
    this.buttonTarget.setAttribute("aria-expanded", "true")

    document.addEventListener("click", this.onDocumentClick)
    document.addEventListener("keydown", this.onKeydown)

    if (this.urlValue && !this.loaded && !this.loading) {
      this.load()
    }
  }

  close() {
    if (this.hasTooltipTarget) {
      this.tooltipTarget.classList.add("hidden")
    }
    if (this.hasButtonTarget) {
      this.buttonTarget.setAttribute("aria-expanded", "false")
    }
    this.popper?.destroy()
    this.popper = null

    document.removeEventListener("click", this.onDocumentClick)
    document.removeEventListener("keydown", this.onKeydown)
  }

  // Dociąga treść helplinka z książki pomocy (raz; po błędzie następne otwarcie próbuje znowu)
  async load() {
    this.loading = true
    try {
      const response = await fetch(this.urlValue, { headers: { Accept: "application/json" } })
      if (!response.ok) {
        throw new Error(`helplink ${response.status}`)
      }
      const data = await response.json()
      this.fill(data)
      this.loaded = true
    } catch (error) {
      console.warn("helplink: nie udało się pobrać treści", error)
      if (this.hasContentTarget) {
        this.contentTarget.textContent = this.contentTarget.dataset.errorText || ""
      }
    } finally {
      this.loading = false
      this.popper?.update()
    }
  }

  // Wypełnia popover odpowiedzią JSON (pola jak w helplinkach Kb): tytuł, HTML treści (z repo,
  // po review - bez sanityzacji po stronie klienta, tak jak strona pomocy) i adres pełnego opisu
  // pod znacznikiem »» w rogu (`path`; bez adresu znacznik zostaje ukryty)
  fill(data) {
    if (this.hasTitleTarget) {
      this.titleTarget.textContent = data.title || ""
      this.titleTarget.classList.toggle("hidden", !data.title)
    }
    if (this.hasContentTarget) {
      this.contentTarget.innerHTML = data.content || ""
    }
    if (this.hasMoreTarget) {
      if (data.path) {
        this.moreTarget.href = data.path
      }
      this.moreTarget.classList.toggle("hidden", !this.moreTarget.getAttribute("href"))
    }
  }

  // Klik poza „?" i popoverem zamyka
  onDocumentClick(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  onKeydown(event) {
    if (event.key === "Escape") {
      this.close()
    }
  }
}
